import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { META_BLUE, MetaBadge, MetaIcon } from './MetaIcon';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import dayjs from 'dayjs';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import {
  IntegrationAccountStatus,
  MetaAdAccountOption,
  MetaHealthResponse,
  MetaImportSummary,
  MetaPageOption,
  connectMetaWithToken,
  getMetaHealth,
  getMetaOptions,
  importMetaHistory,
  selectMetaTargets,
} from '../../../services/marketingLeads';

/**
 * Everything needed to get Meta from "token stored" to "leads arriving":
 *   1. Connect — Facebook OAuth (sign in with the PERSONAL profile, not the
 *      Page profile) or a Business Manager System User token that never
 *      expires.
 *   2. Choose the ad account that runs the campaigns and the Page the lead
 *      forms live on. Saving subscribes the Page to the app's leadgen
 *      webhook — without that Meta never sends a lead.
 *   3. Health check — live, end-to-end, says exactly which piece is broken
 *      and what fixes it.
 */

const GOOD = '#22c55e';
const BAD = '#ef4444';
const WARN = '#f59e0b';

interface MetaSetupDialogProps {
  open: boolean;
  integration: IntegrationAccountStatus | null;
  connecting: boolean;
  onClose: () => void;
  onOAuthConnect: () => void;
  onChanged: () => Promise<void> | void;
}

const sectionTitleSx = { fontSize: 13, fontWeight: 700, color: '#fff', mb: 0.75 };
const helpSx = { fontSize: 12, color: dashboardTokens.textSecondary, lineHeight: 1.55 };
const selectSx = {
  bgcolor: '#1c1c1c',
  color: '#e2e8f0',
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#475569' },
  '& .MuiSvgIcon-root': { color: '#94a3b8' },
};
const menuProps = { PaperProps: { sx: { bgcolor: '#1c1c1c', color: '#e2e8f0', border: `1px solid ${dashboardTokens.border}` } } };

export const MetaSetupDialog: React.FC<MetaSetupDialogProps> = ({
  open,
  integration,
  connecting,
  onClose,
  onOAuthConnect,
  onChanged,
}) => {
  const hasToken = Boolean(integration?.tokenType);
  const setupComplete = Boolean(integration?.setupComplete);

  // ── connect (token paste) ────────────────────────────────────────────────
  const [showConnect, setShowConnect] = useState(!hasToken);
  const [token, setToken] = useState('');
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenMessage, setTokenMessage] = useState<string | null>(null);

  // ── targets ───────────────────────────────────────────────────────────────
  const [adAccounts, setAdAccounts] = useState<MetaAdAccountOption[]>([]);
  const [pages, setPages] = useState<MetaPageOption[]>([]);
  const [adAccountId, setAdAccountId] = useState('');
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsHint, setOptionsHint] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // ── history import ────────────────────────────────────────────────────────
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ message: string; summary: MetaImportSummary } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleImport = async () => {
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await importMetaHistory(365);
      setImportResult(result);
      await onChanged();
    } catch (error: any) {
      setImportError(error?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  // ── health ────────────────────────────────────────────────────────────────
  const [health, setHealth] = useState<MetaHealthResponse | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const result = await getMetaOptions();
      setAdAccounts(result.adAccounts);
      setPages(result.pages);
      setBusinesses(result.businesses || []);
      setOptionsHint(result.hint || null);
      setAdAccountId(result.selected.adAccountId || (result.adAccounts.length === 1 ? result.adAccounts[0].id : ''));
      setPageIds(result.selected.pageIds?.length ? result.selected.pageIds : result.pages.length === 1 ? [result.pages[0].id] : []);
    } catch (error: any) {
      setOptionsError(error?.message || 'Could not load options.');
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const runHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      setHealth(await getMetaHealth());
    } catch (error: any) {
      setHealthError(error?.response?.data?.message || error?.message || 'Health check failed.');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setShowConnect(!hasToken);
    setTokenError(null);
    setTokenMessage(null);
    setSaveError(null);
    setSaveMessage(null);
    setHealth(null);
    setImportResult(null);
    setImportError(null);
    if (hasToken) {
      loadOptions();
      runHealth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasToken]);

  const handleTokenConnect = async () => {
    if (!token.trim()) return;
    setTokenBusy(true);
    setTokenError(null);
    setTokenMessage(null);
    try {
      const result = await connectMetaWithToken(token.trim());
      setToken('');
      setTokenMessage(result.message);
      setAdAccounts(result.adAccounts);
      setPages(result.pages);
      setAdAccountId(result.account.externalAccountId || (result.adAccounts.length === 1 ? result.adAccounts[0].id : ''));
      setPageIds(result.account.pages?.length ? result.account.pages.map((p) => p.id) : result.pages.length === 1 ? [result.pages[0].id] : []);
      setShowConnect(false);
      await onChanged();
      runHealth();
    } catch (error: any) {
      setTokenError(error?.message || 'Meta rejected that token.');
    } finally {
      setTokenBusy(false);
    }
  };

  const handleSave = async () => {
    if (!adAccountId || !pageIds.length) return;
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const result = await selectMetaTargets({ adAccountId, pageIds });
      setSaveMessage(result.message);
      await onChanged();
      runHealth();
    } catch (error: any) {
      setSaveError(error?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const selectedAd = adAccounts.find((a) => a.id === adAccountId);
  const selectedPages = pages.filter((p) => pageIds.includes(p.id));
  const pagesWithoutToken = selectedPages.filter((p) => p.hasToken === false);
  const pagesWithoutAdvertise = selectedPages.filter((p) => !p.canAdvertise);
  const tokenDays = integration?.tokenExpiresInDays;

  const statusChip = (() => {
    if (!hasToken) return { label: 'Not connected', color: '#94a3b8', bg: '#64748b22' };
    if (integration?.reconnectRequired) return { label: 'Reconnect required', color: BAD, bg: '#ef444422' };
    if (!setupComplete) return { label: 'Setup incomplete', color: WARN, bg: '#f59e0b22' };
    if (integration?.lastSyncError) return { label: 'Connected · last sync failed', color: WARN, bg: '#f59e0b22' };
    return { label: 'Connected', color: GOOD, bg: '#22c55e22' };
  })();

  return (
    <Dialog
      open={open}
      onClose={tokenBusy || saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: dashboardTokens.cardBg,
          border: `1px solid ${dashboardTokens.border}`,
          borderRadius: 3,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MetaBadge size={38} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Meta Ads setup</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Chip label={statusChip.label} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: statusChip.bg, color: statusChip.color }} />
              {hasToken && (
                <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textMuted }}>
                  {integration?.tokenType === 'SYSTEM_USER'
                    ? 'System User token · never expires'
                    : tokenDays != null
                      ? `User token · expires in ${tokenDays} day${tokenDays === 1 ? '' : 's'}`
                      : 'User token · Meta reports no expiry'}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }} aria-label="Close" disabled={tokenBusy || saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {integration?.tokenType === 'USER' && tokenDays != null && tokenDays <= 14 && (
          <Alert severity="warning" sx={{ fontSize: 12.5 }}>
            This login token expires in {tokenDays} day{tokenDays === 1 ? '' : 's'}. Reconnect before then, or switch to a System User token so it never expires.
          </Alert>
        )}
        {integration?.lastErrorCode === 'CONFIG' && (
          <Alert severity="error" sx={{ fontSize: 12.5 }}>
            The server is misconfigured, not Meta: {integration.lastSyncError}. Fix <code>.env</code> on the API server and restart it, then press Sync now.
          </Alert>
        )}

        {/* ── 1. Connect ─────────────────────────────────────────────────── */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={sectionTitleSx}>1 · Connect</Typography>
            {hasToken && (
              <Button size="small" onClick={() => setShowConnect((v) => !v)} endIcon={showConnect ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: 'none', color: '#94a3b8', fontSize: 12 }}>
                {showConnect ? 'Hide' : 'Change connection'}
              </Button>
            )}
          </Box>

          <Collapse in={showConnect}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${dashboardTokens.border}`, bgcolor: '#1c1c1c' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', mb: 0.5 }}>Option A · Continue with Facebook</Typography>
                <Typography sx={helpSx}>
                  Sign in with your <b>personal</b> Facebook profile (the one that manages the Nouvak Page and ad account) — not while switched into the Page profile; Facebook Login only works for a person, and the token then reads everything that person has business access to. Approve every permission on the dialog. This token lasts ~60 days.
                </Typography>
                <Button
                  onClick={onOAuthConnect}
                  disabled={connecting || tokenBusy}
                  variant="contained"
                  disableElevation
                  startIcon={<MetaIcon size={16} color="#fff" />}
                  sx={{ mt: 1.25, textTransform: 'none', fontWeight: 700, bgcolor: META_BLUE, '&:hover': { bgcolor: META_BLUE, filter: 'brightness(1.1)' } }}
                >
                  {connecting ? 'Opening…' : hasToken ? 'Reconnect with Facebook' : 'Continue with Facebook'}
                </Button>
              </Box>

              <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${META_BLUE}55`, bgcolor: '#0f1a2b' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <KeyOutlinedIcon sx={{ fontSize: 16, color: '#93c5fd' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Option B · System User token</Typography>
                  <Chip label="Recommended" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#22c55e22', color: GOOD }} />
                </Box>
                <Typography sx={helpSx}>
                  Never expires and isn't tied to anyone's personal login. In <b>Meta Business Suite → Settings → Users → System users</b>: add a system user (Admin), assign it the ad account and the Page with full control, then <b>Generate token</b> for this app with every permission ticked. Paste it here — it's verified with Meta and stored encrypted.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="EAAB…"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  disabled={tokenBusy}
                  sx={{ mt: 1.25, '& .MuiOutlinedInput-root': { bgcolor: '#1c1c1c', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1 }}>
                  <Button
                    onClick={handleTokenConnect}
                    disabled={!token.trim() || tokenBusy}
                    variant="contained"
                    disableElevation
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#5E167A' } }}
                  >
                    {tokenBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Verify & connect'}
                  </Button>
                  {tokenError && <Typography sx={{ fontSize: 12, color: BAD }}>{tokenError}</Typography>}
                  {tokenMessage && <Typography sx={{ fontSize: 12, color: GOOD }}>{tokenMessage}</Typography>}
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* ── 2. Targets ─────────────────────────────────────────────────── */}
        {hasToken && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={sectionTitleSx}>2 · Ad account & Page</Typography>
              <Tooltip title="Reload what the connected login can see">
                <span>
                  <IconButton size="small" onClick={loadOptions} disabled={optionsLoading} sx={{ color: '#94a3b8' }}>
                    {optionsLoading ? <CircularProgress size={14} sx={{ color: '#94a3b8' }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography sx={{ ...helpSx, mb: 1.25 }}>
              Spend and campaign stats come from the ad account. Leads come from the Page(s) your lead forms are attached to — pick every Page that runs a lead form. Saving subscribes each one to lead notifications, which Meta never does by itself.
            </Typography>

            {optionsError && <Alert severity="error" sx={{ mb: 1.25, fontSize: 12.5 }}>{optionsError}</Alert>}
            {!optionsError && !optionsLoading && optionsHint && (
              <Alert severity="warning" sx={{ mb: 1.25, fontSize: 12.5, lineHeight: 1.55 }}>
                <b>Meta returned nothing to choose from.</b> {optionsHint}
                {businesses.length > 0 && (
                  <Box component="span" sx={{ display: 'block', mt: 0.75, color: dashboardTokens.textSecondary }}>
                    Business{businesses.length === 1 ? '' : 'es'} seen: {businesses.map((b) => b.name).join(', ')} — check <b>Business Settings → Ad accounts / Pages → People</b> and add the person who logged in to both assets.
                  </Box>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box>
                <Typography sx={{ fontSize: 11.5, color: '#e2e8f0', mb: 0.5 }}>Ad account</Typography>
                <FormControl fullWidth size="small">
                  <Select value={adAccountId} onChange={(e) => setAdAccountId(String(e.target.value))} displayEmpty sx={selectSx} MenuProps={menuProps} disabled={optionsLoading}>
                    <MenuItem value="" disabled>{adAccounts.length ? 'Choose an ad account' : optionsLoading ? 'Loading…' : 'No ad accounts visible to this login'}</MenuItem>
                    {adAccounts.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name} · {a.id.replace(/^act_/, '')} · {a.currency}{a.businessName ? ` · ${a.businessName}` : ' · personal'}{a.isActive ? '' : ' · NOT ACTIVE'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: 11, color: dashboardTokens.textMuted, mt: 0.5 }}>
                  The number is the account id — match it to the <code>act=</code> in your Ads Manager URL. A personal profile usually has its own empty account with the same name as the business one.
                </Typography>
                {selectedAd && !selectedAd.isActive && (
                  <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.5 }}>This ad account isn't active in Meta (status {selectedAd.accountStatus}) — it won't deliver ads or report spend.</Typography>
                )}
                {selectedAd && selectedAd.currency && selectedAd.currency !== 'GBP' && (
                  <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.5 }}>Billed in {selectedAd.currency}. The dashboard labels spend as £ — figures will be in {selectedAd.currency}.</Typography>
                )}
              </Box>

              <Box>
                <Typography sx={{ fontSize: 11.5, color: '#e2e8f0', mb: 0.5 }}>
                  Facebook Page(s) with lead forms{pageIds.length ? ` · ${pageIds.length} selected` : ''}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={pageIds}
                    onChange={(e) => setPageIds(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
                    displayEmpty
                    sx={selectSx}
                    MenuProps={menuProps}
                    disabled={optionsLoading}
                    renderValue={(selected) => {
                      const ids = selected as string[];
                      if (!ids.length) return <span style={{ color: '#64748b' }}>{pages.length ? 'Choose one or more Pages' : optionsLoading ? 'Loading…' : 'No Pages managed by this login'}</span>;
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {ids.map((id) => {
                            const name =
                              pages.find((x) => x.id === id)?.name ||
                              integration?.pages?.find((x) => x.id === id)?.name ||
                              id;
                            return <Chip key={id} label={name} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#7B1FA233', color: '#e9d5ff' }} />;
                          })}
                        </Box>
                      );
                    }}
                  >
                    {pages.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        <Box component="span" sx={{ width: 16, height: 16, mr: 1, borderRadius: 0.5, border: '1px solid #64748b', bgcolor: pageIds.includes(p.id) ? '#7B1FA2' : 'transparent', flexShrink: 0 }} />
                        {p.name}{p.canAdvertise ? '' : ' · no ADVERTISE access'}{p.hasToken === false ? ' · no Page token' : ''}{p.source && p.source !== 'user' ? ' · via Business' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {pagesWithoutAdvertise.length > 0 && (
                  <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.5 }}>
                    No ADVERTISE task on {pagesWithoutAdvertise.map((p) => p.name).join(', ')} — lead retrieval will be refused there. Grant it in Business Settings → Pages.
                  </Typography>
                )}
                {pagesWithoutToken.length > 0 && (
                  <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.5 }}>
                    Meta issued no Page token for {pagesWithoutToken.map((p) => p.name).join(', ')} — the person who logged in has no task on {pagesWithoutToken.length === 1 ? 'it' : 'them'}, so {pagesWithoutToken.length === 1 ? 'it' : 'they'} can't be subscribed. Add them under Business Settings → Pages → People, then reconnect.
                  </Typography>
                )}
                {integration?.pages?.some((p) => p.subscribeError) && (
                  <Typography sx={{ fontSize: 11.5, color: WARN, mt: 0.5 }}>
                    Last save couldn't subscribe: {integration.pages.filter((p) => p.subscribeError).map((p) => `${p.name} (${p.subscribeError})`).join('; ')}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Button
                  onClick={handleSave}
                  disabled={!adAccountId || !pageIds.length || saving || optionsLoading}
                  variant="contained"
                  disableElevation
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#5E167A' } }}
                >
                  {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `${setupComplete ? 'Save & re-subscribe' : 'Save & subscribe'} ${pageIds.length > 1 ? `${pageIds.length} Pages` : 'Page'}`}
                </Button>
                {saveError && <Typography sx={{ fontSize: 12, color: BAD }}>{saveError}</Typography>}
                {saveMessage && <Typography sx={{ fontSize: 12, color: GOOD }}>{saveMessage}</Typography>}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── 3. Import history ──────────────────────────────────────────── */}
        {hasToken && setupComplete && (
          <Box>
            <Divider sx={{ borderColor: dashboardTokens.border, mb: 1.5 }} />
            <Typography sx={sectionTitleSx}>3 · Import existing history</Typography>
            <Typography sx={{ ...helpSx, mb: 1.25 }}>
              Connecting only captures what happens from now on: the webhook delivers <b>new</b> form submissions, and the nightly sync pulls <b>recent</b> spend. Leads and spend from before today are not in the dashboard until you import them. This pulls every campaign and day-by-day spend for the last 12 months from the ad account above, and every lead Meta still holds (it keeps leads for 90 days) for each Page above. Safe to run again — nothing is duplicated.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                onClick={handleImport}
                disabled={importing}
                variant="contained"
                disableElevation
                startIcon={importing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CloudDownloadOutlinedIcon />}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: META_BLUE, '&:hover': { bgcolor: META_BLUE, filter: 'brightness(1.1)' } }}
              >
                {importing ? 'Importing… this can take a minute' : 'Import history'}
              </Button>
              {importError && <Typography sx={{ fontSize: 12, color: BAD }}>{importError}</Typography>}
            </Box>
            {importResult && (
              <Alert severity={importResult.summary.warnings.length ? 'warning' : 'success'} sx={{ mt: 1.25, fontSize: 12.5, lineHeight: 1.55 }}>
                <b>{importResult.message}</b>
                {importResult.summary.adAccountId && (
                  <Box component="div" sx={{ mt: 0.25, color: dashboardTokens.textSecondary }}>
                    Ad account: {importResult.summary.adAccountName || ''} ({importResult.summary.adAccountId.replace(/^act_/, '')})
                  </Box>
                )}
                <Box component="div" sx={{ mt: 0.5 }}>
                  Spend {importResult.summary.spend.toLocaleString()} across {importResult.summary.statDays} campaign-days · Meta reported {importResult.summary.reportedLeads} lead{importResult.summary.reportedLeads === 1 ? '' : 's'} · {importResult.summary.forms} form{importResult.summary.forms === 1 ? '' : 's'} scanned
                </Box>
                {importResult.summary.pages.map((p) => (
                  <Box component="div" key={p.id} sx={{ mt: 0.25, color: p.error ? BAD : 'inherit' }}>
                    {p.name}: {p.error ? p.error : `${p.forms} form${p.forms === 1 ? '' : 's'}, ${p.leadsCreated} new of ${p.leadsSeen} lead${p.leadsSeen === 1 ? '' : 's'}`}
                  </Box>
                ))}
                {importResult.summary.warnings.map((w, i) => (
                  <Box component="div" key={i} sx={{ mt: 0.25, color: '#fbbf24' }}>⚠ {w}</Box>
                ))}
                {importResult.summary.reportedLeads > importResult.summary.leadsSeen && (
                  <Box component="div" sx={{ mt: 0.5, color: dashboardTokens.textSecondary }}>
                    Meta counts {importResult.summary.reportedLeads} leads in its insights but only {importResult.summary.leadsSeen} were retrievable — the rest are older than Meta's 90-day retention, or on a form belonging to a Page not chosen above.
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        )}

        {/* ── 4. Health ──────────────────────────────────────────────────── */}
        {hasToken && (
          <Box>
            <Divider sx={{ borderColor: dashboardTokens.border, mb: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography sx={sectionTitleSx}>{setupComplete ? '4' : '3'} · Health check</Typography>
              <Button size="small" onClick={runHealth} disabled={healthLoading} startIcon={healthLoading ? <CircularProgress size={12} sx={{ color: '#94a3b8' }} /> : <RefreshIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', color: '#94a3b8', fontSize: 12 }}>
                {healthLoading ? 'Checking…' : 'Re-run'}
              </Button>
            </Box>
            {healthError && <Alert severity="error" sx={{ fontSize: 12.5 }}>{healthError}</Alert>}
            {health && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {health.checks.map((c) => (
                  <Box key={c.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1, borderRadius: 1.5, bgcolor: c.ok ? 'transparent' : '#ef44440d', border: `1px solid ${c.ok ? dashboardTokens.border : '#ef444444'}` }}>
                    {c.ok ? <CheckCircleIcon sx={{ fontSize: 16, color: GOOD, mt: 0.2 }} /> : <CancelIcon sx={{ fontSize: 16, color: BAD, mt: 0.2 }} />}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#e2e8f0' }}>{c.label}</Typography>
                      {c.detail && <Typography sx={{ fontSize: 11.5, color: dashboardTokens.textSecondary, wordBreak: 'break-word' }}>{c.detail}</Typography>}
                      {!c.ok && c.fix && <Typography sx={{ fontSize: 11.5, color: '#fbbf24', mt: 0.25 }}>Fix: {c.fix}</Typography>}
                    </Box>
                  </Box>
                ))}
                <Typography sx={{ fontSize: 11, color: dashboardTokens.textMuted, mt: 0.5 }}>
                  Checked {dayjs(health.checkedAt).format('D MMM, HH:mm:ss')}
                  {health.urls.webhook && ` · webhook ${health.urls.webhook}`}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={onClose} disabled={tokenBusy || saving} sx={{ textTransform: 'none', color: '#94a3b8', fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
